import { useState, useCallback } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ThreadList } from './ThreadList';
import { ConversationView } from './ConversationView';
import { ThreadContextPanel } from './ThreadContextPanel';
import type { ConversationThread } from '@/types/omniChannel';
import { useIsMobile } from '@/hooks/use-mobile';

export function OmniChannelTab() {
  const [selectedThread, setSelectedThread] = useState<ConversationThread | null>(null);
  const isMobile = useIsMobile();

  const handleSelectThread = useCallback((thread: ConversationThread) => {
    setSelectedThread(thread);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedThread(null);
  }, []);

  // Mobile: Show either thread list or conversation, not both
  if (isMobile) {
    if (selectedThread) {
      return (
        <div className="flex h-[calc(100vh-16rem)] flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          {/* Back button header */}
          <div className="border-b border-border p-2">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to conversations
            </Button>
          </div>
          <ConversationView thread={selectedThread} />
        </div>
      );
    }

    return (
      <div className="h-[calc(100vh-16rem)] overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <ThreadList
          selectedThreadId={null}
          onSelectThread={handleSelectThread}
        />
      </div>
    );
  }

  // Desktop: Three-pane split view (thread list / conversation / context panel)
  return (
    <div className="grid h-[calc(100vh-16rem)] min-h-[560px] grid-cols-[320px_1fr] overflow-hidden rounded-lg border border-border bg-card shadow-sm xl:grid-cols-[320px_1fr_280px]">
      {/* Thread list - left panel */}
      <div className="flex min-h-0 flex-col overflow-hidden">
        <ThreadList
          selectedThreadId={selectedThread?.id || null}
          onSelectThread={handleSelectThread}
        />
      </div>

      {/* Conversation - centre panel */}
      <div className="flex min-h-0 min-w-0 flex-col">
        <ConversationView thread={selectedThread} />
      </div>

      {/* Context panel - right panel (xl and up) */}
      <aside className="hidden min-h-0 flex-col overflow-hidden border-l border-border xl:flex">
        <ThreadContextPanel thread={selectedThread} />
      </aside>
    </div>
  );
}
